import React, { ComponentProps, Fragment } from "react";
import {
  Select,
  SelectItem,
  SelectSeparator,
} from "@clarkinc/zest-design-system/ui";
import { FilteredBy, VariationOption } from "../../reviewModels";
import { allVariations } from "../../CustomerReviewHelper";
import ChildOrNull from "../../../ChildOrNull";
import {
  clearGa4EventInDataLayer,
  sendGtmEvent,
} from "~/analytics/definitions";

type MultipleVariationFilterProps = ComponentProps<"div"> & {
  onVariationTypeChange: (filteredBy: FilteredBy) => void;
  variationOptions?: VariationOption[];
  filteredBy: FilteredBy;
};

const MultipleVariationFilter = ({
  onVariationTypeChange,
  variationOptions = [],
  filteredBy,
  className,
}: MultipleVariationFilterProps) => {
  const variationTypeChangeHandler = (selectedValue: string) => {
    const [value, type] = selectedValue.split(":");
    const variation = {
      type: type ?? "",
      value: value ?? "",
    };

    const variationValue = value ?? "";
    const gtmLabel = type ? `${type} - ${variationValue}` : variationValue;

    if (gtmLabel) {
      clearGa4EventInDataLayer();
      sendGtmEvent({
        event: "ga4_event",
        eventName: "Customer Reviews - Filter By Dropdown Change",
        eventLabel: `Filter By: ${gtmLabel}`,
      });
    }

    onVariationTypeChange({ ...filteredBy, variation });
  };

  const getFilterLabel = () => {
    let label = "Filter By";

    if (variationOptions) {
      const variationTypes = Array.from(
        new Set(variationOptions.map((option) => option.type)),
      );

      if (variationTypes.length === 1) {
        label += ` ${variationTypes[0]}`;
      }
    }

    return label;
  };

  const hasMultipleVariationTypes = () => {
    if (!variationOptions) {
      return false;
    }

    const variationTypes = Array.from(
      new Set(variationOptions.map((option) => option.type)),
    );

    return variationTypes.length > 1;
  };

  const shouldShowVariationDropdown =
    variationOptions?.length > 0 &&
    variationOptions.flatMap((opt) => opt.values).length > 1;

  const selectValue =
    filteredBy.variation.value && filteredBy.variation.type
      ? `${filteredBy.variation.value}:${filteredBy.variation.type}`
      : allVariations;

  return (
    <ChildOrNull condition={shouldShowVariationDropdown}>
      <div
        className={`float-left mr-0 mt-[7px] xs:mt-0 px-4 xs:px-0 w-full box-border ${className}`}
      >
        <Select
          id="filterBy"
          name="filterBy"
          value={selectValue}
          onValueChange={variationTypeChangeHandler}
          data-testid="review-filter-dropdown"
          label={getFilterLabel()}
          theme="wss"
          className="suffix_dropdown configure item_number text-sm w-full lg:w-full"
        >
          <SelectItem value={allVariations}>All Options </SelectItem>
          {variationOptions.map((variation, idx) => (
            <Fragment key={variation.type}>
              {hasMultipleVariationTypes() && idx > 0 && <SelectSeparator />}
              {variation.values.map((value) => (
                <SelectItem
                  key={`${variation.type}:${value}`}
                  value={`${value}:${variation.type}`}
                >
                  {hasMultipleVariationTypes()
                    ? `${variation.type} - ${value}`
                    : value}
                </SelectItem>
              ))}
            </Fragment>
          ))}
        </Select>
      </div>
    </ChildOrNull>
  );
};

export default MultipleVariationFilter;
